import React, { useState } from "react";
import {
  ExclamationTriangleIcon,
  XCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

/**
 * ErrorBanner - Inline error display for persistent errors requiring user action
 *
 * @param {string} message - Error message to display
 * @param {string} [variant] - "soft" (amber, default) or "critical" (red)
 * @param {function} [onRetry] - Optional callback for retry action
 * @param {string} [retryLabel] - Custom label for retry button (default: "Try Again")
 * @param {boolean} [dismissible] - Whether banner can be dismissed (default: true)
 * @param {function} [onDismiss] - Called when banner is dismissed
 * @param {string} [className] - Additional CSS classes
 */
export default function ErrorBanner({
  message,
  variant = "soft",
  onRetry,
  retryLabel = "Try Again",
  dismissible = true,
  onDismiss,
  className = "",
}) {
  const [isRetrying, setIsRetrying] = useState(false);

  const variants = {
    soft: {
      containerClass:
        "bg-amber-50 border-amber-300 dark:bg-amber-950 dark:border-amber-700",
      iconClass: "text-amber-600 dark:text-amber-400",
      textClass: "text-amber-800 dark:text-amber-200",
      buttonClass:
        "text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900",
      retryClass: "bg-amber-500 hover:bg-amber-600 text-white",
      Icon: ExclamationTriangleIcon,
    },
    critical: {
      containerClass:
        "bg-red-50 border-red-300 dark:bg-red-950 dark:border-red-700",
      iconClass: "text-red-600 dark:text-red-400",
      textClass: "text-red-800 dark:text-red-200",
      buttonClass:
        "text-red-700 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-900",
      retryClass: "bg-red-500 hover:bg-red-600 text-white",
      Icon: XCircleIcon,
    },
  };

  const config = variants[variant] || variants.soft;
  const Icon = config.Icon;

  const handleRetry = async () => {
    if (onRetry) {
      setIsRetrying(true);
      try {
        await onRetry();
      } finally {
        setIsRetrying(false);
      }
    }
  };

  return (
    <div
      className={`rounded-lg border px-4 py-3 ${config.containerClass} ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${config.iconClass}`} />

        <div className="flex-1 min-w-0">
          <p className={`text-sm ${config.textClass}`}>{message}</p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {onRetry && (
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${config.retryClass}`}
            >
              {isRetrying ? (
                <ArrowPathIcon className="h-3 w-3 animate-spin" />
              ) : (
                retryLabel
              )}
            </button>
          )}

          {dismissible && onDismiss && (
            <button
              onClick={onDismiss}
              className={`p-1 rounded transition-colors ${config.buttonClass}`}
              aria-label="Dismiss"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
